import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import CopyToken from "./images/copy_token.png";
import CreateToken from "./images/create_token_btn.png";
import TokenName from "./images/token_name.png";
import OpenAIConnectForm from "./OpenAIConnectForm";

const OpenAIIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("openai");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerOpenAIIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerOpenAIIntegration(values.api_key)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("OpenAI integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, Breeze Security will be able to use your OpenAI API key where relevant."
            : ""
        }
        image={logos.OpenAILogo}
        name={"OpenAI"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Typography variant={"h4"}>OpenAI is integrated!</Typography>
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <GuidelineStepByStep>
                <GuidelineStep title="Open your OpenAI Admin API keys page.">
                  <PrimaryButton
                    sx={{ width: "fit-content" }}
                    href="https://platform.openai.com/settings/organization/admin-keys"
                    target="_blank"
                  >
                    Go to OpenAI Admin API keys page
                  </PrimaryButton>
                </GuidelineStep>
                <GuidelineStep title="Create or select an Admin API key for Breeze Security.">
                  <GuidelineInstruction text="Use an existing OpenAI Admin API key or create a new one dedicated to Breeze Security. If you already have a key, verify that it is securely stored and has the required admin permissions. Otherwise, create a new Admin API key before continuing." />

                  <ScreenshotAccordion
                    images={[
                      {
                        file: CreateToken,
                        altext: "Create Token Button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                    Click the 'Create new Admin key' button to generate a new
                    API key for Breeze Security.
                  </Typography>

                  <ScreenshotAccordion
                    images={[
                      {
                        file: TokenName,
                        altext: "Token Name",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ mt: 1, mb: 2 }}
                  >
                    <Typography variant="body2">
                      Suggested token name:{" "}
                      <strong>BreezeSecurityAdminApiToken</strong>
                    </Typography>
                    <CustomCopyButton
                      copyTitle="Token Name"
                      copyText="BreezeSecurityAdminApiToken"
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    component="ol"
                    sx={{ mt: 1, mb: 2, pl: 2 }}
                  >
                    <li>
                      Enter the token name (or click the copy button above to
                      use BreezeSecurityAdminApiToken).
                    </li>
                    <li>Select the Read only role.</li>
                    <li>Click Create admin key to finish.</li>
                  </Typography>

                  <ScreenshotAccordion
                    images={[
                      {
                        file: CopyToken,
                        altext: "Copy Token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                    Copy the API key immediately after creation. You won't be
                    able to see it again after closing this dialog.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Paste the Admin API key below.">
                  <Box width={700}>
                    <OpenAIConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default OpenAIIntegration;
